"use client";

import { useRouter } from "next/navigation";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { mutate } from "swr";
import { BellAlertIcon } from "@heroicons/react/20/solid";
import { useNotificationStore } from "@store/notificationStore";
import { getCookieValue, joinClassNames } from "@utils/helpers";
import { useHydrated, useLatestCookieValue, useWindowSize } from "@utils/hooks";
import { BASE_URL, COUNTRY_CODE } from "@utils/constants";
import { Edgeconfig } from "@types";

const BANNER_SCROLL_VELOCITY = 0.105;

interface Props {
  isDraftMode?: boolean;
  banner?: Edgeconfig["banner"];
}

export default function Banner({ isDraftMode, banner }: Props) {
  const router = useRouter();
  const windowsize = useWindowSize();
  const { setFailure, setSuccess } = useNotificationStore((state) => state);
  const clarityUserId = useLatestCookieValue("_clck")?.split("%")[0];
  const websiteUid = getCookieValue("itk-website-uid");
  const hydrated = useHydrated();

  if (!hydrated && banner?.enabled) {
    return null;
  }

  return (
    <AnimatePresence>
      {isDraftMode ? (
        <motion.section
          initial="hidden"
          animate="show"
          exit="hidden"
          variants={{
            hidden: {
              height: 0,
              transition: {
                duration: 0.125,
              },
            },
            show:
              windowsize.width && windowsize.width < 640
                ? { height: "106px" }
                : { height: "50px" },
          }}
          transition={{ duration: 0.25 }}
          className="w-full bg-yellow-200"
        >
          <motion.div
            initial="hidden"
            animate="show"
            exit="hidden"
            variants={{
              hidden: { opacity: 0, transition: { duration: 0.125 } },
              show: { opacity: 1 },
            }}
            transition={{ duration: 0.25 }}
            className="mx-auto flex flex-col gap-2 sm:flex-row max-w-7xl items-center justify-between p-2 lg:px-8"
          >
            <p className="text-yellow-800">
              You are viewing this event in draft mode. Do not use this link
              publicly.
            </p>
            <button
              className="flex w-full sm:w-fit gap-2 items-center justify-center disabled:bg-yellow-200 disabled:cursor-not-allowed rounded-md border border-transparent bg-yellow-500 text-yellow-800 px-2 py-1 text-base font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
              onClick={async () => {
                const res = await axios.get("/api/disable-draft");

                if (res.status !== 200) return;

                const basketKey = getCookieValue("ASP.NET_SessionId");

                if (!basketKey) {
                  setSuccess(`Draft Mode Disabled`);
                  router.refresh();
                  return;
                }

                const response = await axios.delete(
                  `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/basket/${basketKey}`,
                  {
                    headers: {
                      "itk-website-uid": websiteUid,
                    },
                  }
                );

                if (response.status >= 200 && response.status < 300) {
                  setSuccess(`Draft Mode Disabled`);
                  await axios.delete("/api/basket");
                  await mutate(`basket/${basketKey}`);
                  await mutate(`basket/${basketKey}/summary`);
                  await mutate(`basket/${basketKey}/checkout`);
                  await mutate(
                    (key: any) =>
                      Array.isArray(key) &&
                      key[0] === `basket/${basketKey}/checkout`
                  );
                  await mutate(
                    (key) =>
                      typeof key === "string" && key.startsWith("events/"),
                    undefined,
                    {
                      revalidate: true,
                    }
                  );
                } else if (response.status >= 400 && response.status < 500) {
                  setFailure(response.data.message);
                } else {
                  setFailure("Something went wrong.", true);
                }

                router.refresh();
              }}
            >
              Disable Draft
            </button>
          </motion.div>
        </motion.section>
      ) : banner?.enabled ? (
        <section
          className={joinClassNames(
            "w-full",
            banner?.type === "green"
              ? "animate-notice-border-green"
              : banner?.type === "yellow"
              ? "animate-notice-border-yellow"
              : "animate-notice-border-red"
          )}
        >
          <a
            href={
              clarityUserId
                ? `${banner?.link}?entry.2100692719=${clarityUserId}`
                : banner?.link
            }
            target="_blank"
            className={joinClassNames(
              "flex items-center gap-4 backdrop-blur-3xl text-sm font-medium w-full h-12 whitespace-nowrap",
              banner?.type === "green"
                ? "text-emerald-800"
                : banner?.type === "yellow"
                ? "text-yellow-800"
                : "text-red-300 bg-black/40"
            )}
          >
            <p
              style={{
                animationDuration: `${
                  banner?.text.length * BANNER_SCROLL_VELOCITY
                }s`,
              }}
              className="flex items-center gap-4 animate-marquee w-fit will-change-transform"
            >
              {banner?.text}
              <BellAlertIcon className="inline h-5 w-5" />
            </p>
            <p
              style={{
                animationDuration: `${
                  banner?.text.length * BANNER_SCROLL_VELOCITY
                }s`,
              }}
              className="flex items-center gap-4 animate-marquee w-fit will-change-transform"
            >
              {banner?.text}
              <BellAlertIcon className="inline h-5 w-5" />
            </p>
            <p
              style={{
                animationDuration: `${
                  banner?.text.length * BANNER_SCROLL_VELOCITY
                }s`,
              }}
              className="flex items-center gap-4 animate-marquee w-fit will-change-transform"
            >
              {banner?.text}
              <BellAlertIcon className="inline h-5 w-5" />
            </p>
            <p
              style={{
                animationDuration: `${
                  banner?.text.length * BANNER_SCROLL_VELOCITY
                }s`,
              }}
              className="flex items-center gap-4 animate-marquee w-fit will-change-transform"
            >
              {banner?.text}
              <BellAlertIcon className="inline h-5 w-5" />
            </p>
            <p
              style={{
                animationDuration: `${
                  banner?.text.length * BANNER_SCROLL_VELOCITY
                }s`,
              }}
              className="flex items-center gap-4 animate-marquee w-fit will-change-transform"
            >
              {banner?.text}
              <BellAlertIcon className="inline h-5 w-5" />
            </p>
            <p
              style={{
                animationDuration: `${
                  banner?.text.length * BANNER_SCROLL_VELOCITY
                }s`,
              }}
              className="flex items-center gap-4 animate-marquee w-fit will-change-transform"
            >
              {banner?.text}
              <BellAlertIcon className="inline h-5 w-5" />
            </p>
            <p
              style={{
                animationDuration: `${
                  banner?.text.length * BANNER_SCROLL_VELOCITY
                }s`,
              }}
              className="flex items-center gap-4 animate-marquee w-fit will-change-transform"
            >
              {banner?.text}
              <BellAlertIcon className="inline h-5 w-5" />
            </p>
            <p
              style={{
                animationDuration: `${
                  banner?.text.length * BANNER_SCROLL_VELOCITY
                }s`,
              }}
              className="flex items-center gap-4 animate-marquee w-fit will-change-transform"
            >
              {banner?.text}
              <BellAlertIcon className="inline h-5 w-5" />
            </p>
          </a>
        </section>
      ) : (
        <></>
      )}
    </AnimatePresence>
  );
}
